import { IntlShape } from 'react-intl'
import { MilestoneCamera } from '@/types/vlm'
import { PipelineFormValues } from '../VlmPipelineForm'
import { validateMaxCameras } from '../cameras/validateMaxCameras'
import {
  PatrolTimeFormValue,
  getPatrolTimeValues
} from '../pipelineSourceValues'
import {
  PATROL_MAX_ROUND_TIMEOUT_MINUTES,
  PATROL_MAX_TIMES,
  PATROL_MIN_ROUND_TIMEOUT_MINUTES,
  formatDuration,
  isValidPatrolTime,
  minPatrolGapSeconds,
  roundTimeoutToSeconds
} from './patrolSchedule'

const isPatrol = (values: PipelineFormValues) =>
  values.pipelineType === 'virtual_patrol'

const isMilestonePatrol = (values: PipelineFormValues) =>
  isPatrol(values) && values.source.patrolVideoSource === 'milestone'

const isFixedTimes = (values: PipelineFormValues) =>
  isPatrol(values) && values.source.patrolScheduleMode === 'at'

const isInterval = (values: PipelineFormValues) =>
  isPatrol(values) && values.source.patrolScheduleMode === 'every'

export const getScheduleGapSeconds = (values: PipelineFormValues) => {
  const { patrolScheduleMode, patrolTimes, patrolInterval } = values.source
  return minPatrolGapSeconds(
    patrolScheduleMode === 'at'
      ? { mode: 'at', at: getPatrolTimeValues(patrolTimes) }
      : { mode: 'every', every: patrolInterval }
  )
}

// Mirrors the backend's validation of the virtual patrol source.
export const getPatrolSourceValidators = (intl: IntlShape) => ({
  patrolServerId: (value: string, values: PipelineFormValues) =>
    isMilestonePatrol(values) && !value
      ? intl.formatMessage({ id: 'vlm.pipelines.create.milestone.required' })
      : null,

  patrolCameras: (value: MilestoneCamera[], values: PipelineFormValues) => {
    if (!isPatrol(values)) return null

    if (value.length === 0)
      return intl.formatMessage({
        id: 'vlm.pipelines.create.patrol.cameras.required'
      })

    return validateMaxCameras(intl, value)
  },

  patrolTimes: (times: PatrolTimeFormValue[], values: PipelineFormValues) => {
    if (!isFixedTimes(values)) return null

    const value = getPatrolTimeValues(times)

    if (value.length === 0)
      return intl.formatMessage({
        id: 'vlm.pipelines.create.patrol.schedule.times.required'
      })

    if (value.length > PATROL_MAX_TIMES)
      return intl.formatMessage(
        { id: 'vlm.pipelines.create.patrol.schedule.times.max' },
        { max: PATROL_MAX_TIMES }
      )

    if (!value.every(isValidPatrolTime))
      return intl.formatMessage({
        id: 'vlm.pipelines.create.patrol.schedule.times.invalid'
      })

    if (new Set(value).size !== value.length)
      return intl.formatMessage({
        id: 'vlm.pipelines.create.patrol.schedule.times.unique'
      })

    return null
  },

  patrolStartTime: (value: string, values: PipelineFormValues) =>
    isInterval(values) && !isValidPatrolTime(value)
      ? intl.formatMessage({
          id: 'vlm.pipelines.create.patrol.schedule.start.invalid'
        })
      : null,

  patrolTimezone: (value: string, values: PipelineFormValues) =>
    (isFixedTimes(values) || isInterval(values)) && !value
      ? intl.formatMessage({
          id: 'vlm.pipelines.create.patrol.schedule.timezone.required'
        })
      : null,

  patrolRoundTimeoutMinutes: (value: number, values: PipelineFormValues) => {
    if (!isPatrol(values)) return null

    if (
      !Number.isInteger(value) ||
      value < PATROL_MIN_ROUND_TIMEOUT_MINUTES ||
      value > PATROL_MAX_ROUND_TIMEOUT_MINUTES
    )
      return intl.formatMessage(
        { id: 'vlm.pipelines.create.patrol.schedule.timeout.range' },
        {
          min: PATROL_MIN_ROUND_TIMEOUT_MINUTES,
          max: PATROL_MAX_ROUND_TIMEOUT_MINUTES
        }
      )

    // A round that comes due while the previous one still runs is skipped,
    // and no summary is sent for it (audit VP-4).
    const gap = getScheduleGapSeconds(values)
    if (roundTimeoutToSeconds(value) >= gap)
      return intl.formatMessage(
        { id: 'vlm.pipelines.create.patrol.schedule.timeout.gap' },
        { gap: formatDuration(gap, intl) }
      )

    return null
  }
})
